import React, { FC, useState } from "react";
import { useParams } from "react-router-dom";
import { change, getFormValues, isPristine } from "@mds/common/components/forms/form";
import { Col, Row } from "antd";
import { IMineIncident } from "@mds/common/interfaces";
import { getDropdownInspectors } from "@mds/common/redux/slices/partiesSlice";
import {
  getDangerousOccurrenceSubparagraphOptions,
  getDropdownIncidentDeterminationOptions,
  getDropdownIncidentFollowupActionOptions,
  getDropdownIncidentStatusCodeOptions,
  getIncidentStatusCodeHash,
} from "@mds/common/redux/selectors/staticContentSelectors";
import * as FORM from "@/constants/forms";
import {
  documentNameColumn,
  uploadDateColumn,
  uploadedByColumn,
} from "@mds/common/components/documents/DocumentColumns";
import IncidentFormDocuments from "@/components/Forms/incidents/IncidentFormDocuments";
import IncidentFormInternalDocumentComments from "@/components/Forms/incidents/IncidentFormInternalDocumentComments";
import IncidentFormInitialReport from "@/components/Forms/incidents/IncidentFormInitialReport";

import IncidentFormUpdateIncidentStatus from "@/components/Forms/incidents/IncidentFormUpdateIncidentStatus";
import IncidentFormMinistryFollowup from "@/components/Forms/incidents/IncidentFormMinistryFollowup";
import { removeDocumentFromMineIncident } from "@mds/common/redux/actionCreators/incidentActionCreator";
import FormWrapper from "@mds/common/components/forms/FormWrapper";
import RenderSubmitButton from "@mds/common/components/forms/RenderSubmitButton";
import { useAppDispatch, useAppSelector } from "@mds/common/redux/rootState";

export const INITIAL_INCIDENT_DOCUMENTS_FORM_FIELD = "initial_incident_documents";
export const FINAL_REPORT_DOCUMENTS_FORM_FIELD = "final_report_documents";
export const INTERNAL_MINISTRY_DOCUMENTS_FORM_FIELD = "internal_ministry_documents";

export const documentColumns = [documentNameColumn(), uploadDateColumn(), uploadedByColumn()];

export const formatDocumentRecords = (documents) =>
  documents?.map((doc) => ({ ...doc, key: doc.mine_document_guid }));

interface IncidentFormProps {
  initialValues: IMineIncident;
  isEditMode: boolean;
  incident: IMineIncident;
  handlers: {
    handleSaveData: () => void;
    handleFetchData: () => void;
  };
}

export const IncidentForm: FC<IncidentFormProps> = (props) => {
  const { mineIncidentGuid, mineGuid } = useParams<{
    mineGuid: string;
    mineIncidentGuid: string;
  }>();

  const { isEditMode, handlers: parentHandlers } = props;
  const isNewIncident = Boolean(!mineIncidentGuid);

  const dispatch = useAppDispatch();

  const incidentDeterminationOptions = useAppSelector(getDropdownIncidentDeterminationOptions);
  const incidentStatusCodeHash = useAppSelector(getIncidentStatusCodeHash);
  const dangerousOccurenceSubparagraphOptions = useAppSelector(
    getDangerousOccurrenceSubparagraphOptions
  );
  const incidentFollowUpActionOptions = useAppSelector(getDropdownIncidentFollowupActionOptions);
  const inspectorOptions = useAppSelector(getDropdownInspectors) || [];

  const formValues = useAppSelector(getFormValues(FORM.ADD_EDIT_INCIDENT)) as Partial<IMineIncident>;
  const dropdownIncidentStatusCodeOptions = useAppSelector(getDropdownIncidentStatusCodeOptions);
  const pristine = useAppSelector(isPristine(FORM.ADD_EDIT_INCIDENT));

  const [uploadedFiles, setUploadedFiles] = useState([]);

  const onFileLoad = (fileName, document_manager_guid, documentTypeCode, documentFormField) => {
    const updatedUploadedFiles = [
      ...uploadedFiles,
      {
        document_name: fileName,
        document_manager_guid,
        mine_incident_document_type_code: documentTypeCode,
      },
    ];
    setUploadedFiles(updatedUploadedFiles);
    dispatch(change(FORM.ADD_EDIT_INCIDENT, documentFormField, updatedUploadedFiles));
  };

  const onRemoveFile = (err, fileItem, documentFormField) => {
    const updatedUploadedFiles = uploadedFiles.filter(
      (doc) => doc.document_manager_guid !== fileItem.serverId
    );
    setUploadedFiles(updatedUploadedFiles);
    return dispatch(change(FORM.ADD_EDIT_INCIDENT, documentFormField, updatedUploadedFiles));
  };

  const handleDeleteDocument = (event, mineDocumentGuid: string) => {
    if (mineGuid && props.incident.mine_incident_guid && mineDocumentGuid) {
      return dispatch(
        removeDocumentFromMineIncident(
          mineGuid,
          props.incident.mine_incident_guid,
          mineDocumentGuid
        )
      ).then(() => props.handlers.handleFetchData());
    }
    return null;
  };

  const renderEditSaveControls = () => {
    return (
      <div className="right center-mobile violet">
        {isEditMode && (
          <RenderSubmitButton buttonProps={{ id: "mine-incident-submit" }} buttonText={isNewIncident ? "Create Incident" : "Save Changes"} />
        )}
      </div>
    );
  };

  return (
    <FormWrapper
      name={FORM.ADD_EDIT_INCIDENT}
      initialValues={props.initialValues}
      reduxFormConfig={{
        enableReinitialize: true,
        touchOnBlur: true,
        touchOnChange: true,
        destroyOnUnmount: true,
      }}
      onSubmit={parentHandlers.handleSaveData}>
      <Col span={24}>
        <IncidentFormUpdateIncidentStatus
          incident={props.incident}
          dropdownIncidentStatusCodeOptions={dropdownIncidentStatusCodeOptions}
          incidentStatusCodeHash={incidentStatusCodeHash}
          isEditMode={isEditMode}
          formValues={formValues}
          pristine={pristine}
        />
      </Col>
      <Row>
        <Col span={24}>{renderEditSaveControls()}</Col>
        <Col span={16} offset={4}>
          <IncidentFormInitialReport
            incident={props.incident}
            isEditMode={isEditMode}
            inspectorOptions={inspectorOptions}
          />
          <br />
          <IncidentFormDocuments
            documents={formValues?.documents ?? []}
            isEditMode={isEditMode}
            onFileLoad={onFileLoad}
            onDeleteDocument={handleDeleteDocument}
            onRemoveFile={onRemoveFile}
          />
          <br />
          <IncidentFormMinistryFollowup
            isEditMode={isEditMode}
            incidentDeterminationOptions={incidentDeterminationOptions}
            incidentFollowUpActionOptions={incidentFollowUpActionOptions}
            dangerousOccurenceSubparagraphOptions={dangerousOccurenceSubparagraphOptions}
            inspectorOptions={inspectorOptions}
          />
          <br />
          <IncidentFormInternalDocumentComments
            documents={formValues?.documents ?? []}
            incident={props.incident}
            isEditMode={isEditMode}
            onFileLoad={onFileLoad}
            onDeleteDocument={handleDeleteDocument}
            formValues={formValues}
            onRemoveFile={onRemoveFile}
          />
        </Col>
        <Col span={24}>
          <br />
          {renderEditSaveControls()}
        </Col>
      </Row>
    </FormWrapper>
  );
};

export default IncidentForm;
